// src/components/admin/tabs/OverviewTab.jsx
// 系统概览：指标卡 + 费用 Top 用户 + 错误数。数据来自 fetchOverview，逻辑原样保留。
import React, { useState, useEffect, useCallback } from "react";
import { fetchOverview } from "../../../services/adminService";
import {
  Panel, StatCard, EmptyText, ErrorNotice, Segmented, AdminScopeFilterBar,
} from "../adminUi.jsx";
import { displayUserId, useAdminScope } from "../adminUtils";

const ACCENT = "var(--color-warning)";

const RANGE_OPTIONS = [
  { key: 'today', label: '今日' },
  { key: '7d', label: '近 7 日' },
  { key: '30d', label: '近 30 日' },
  { key: 'all', label: '全部' },
];

const OverviewTab = ({ projectId, projectOptions }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [range, setRange] = useState('today');
  const [scope, setScope] = useAdminScope(projectId);

  const getRangeTimestamps = useCallback((r) => {
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    if (r === 'today') {
      const d = new Date(); d.setHours(0, 0, 0, 0);
      return [d.getTime(), now];
    }
    if (r === '7d') return [now - 7 * day, now];
    if (r === '30d') return [now - 30 * day, now];
    return [null, now];
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [start, end] = getRangeTimestamps(range);
      const res = await fetchOverview(start, end, scope.projectId, scope.userId);
      setData(res);
    } catch (e) {
      console.error(e);
      setError(e?.message || '概览加载失败');
    } finally { setLoading(false); }
  }, [range, scope.projectId, scope.userId, getRangeTimestamps]);

  useEffect(() => { load(); }, [load]);

  const firstLoading = loading && !data;

  const statItems = [
    { label: '用户总数', value: data?.totalUserCount ?? '-' },
    { label: '活跃用户', value: data?.activeAppUserCount ?? '-' },
    { label: '项目数', value: data?.projectCount ?? '-' },
    { label: 'AI任务数', value: data?.aiTaskCount ?? '-' },
    { label: '图片生成', value: data?.imageCount ?? '-' },
    { label: '视频生成', value: data?.videoCount ?? '-' },
    { label: '费用 (¥)', value: data?.totalCost ?? '-', color: ACCENT },
    { label: 'AI任务费用 (¥)', value: data?.taskCost ?? '-', color: ACCENT },
    { label: '生成活跃用户', value: data?.activeUserCount ?? '-' },
    { label: '操作错误', value: data?.failedCount ?? '-', color: 'var(--color-error)' },
    { label: 'AI任务错误', value: data?.failedTaskCount ?? '-', color: 'var(--color-error)' },
  ];

  return (
    <div>
      <AdminScopeFilterBar
        scope={scope}
        setScope={setScope}
        projectOptions={projectOptions}
        onRefresh={load}
        loading={loading}
      >
        <div>
          <label className="text-label block mb-1.5">时间范围</label>
          <Segmented options={RANGE_OPTIONS} value={range} onChange={setRange} ariaLabel="时间范围" />
        </div>
      </AdminScopeFilterBar>
      {error && <ErrorNotice message={error} />}
      {firstLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 68 }} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {statItems.map((item) => (
            <StatCard key={item.label} label={item.label} value={item.value} color={item.color} />
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="费用 Top 用户" sub="按时间范围聚合">
          {firstLoading ? (
            <div className="space-y-2">
              <div className="skeleton h-3 w-full" />
              <div className="skeleton h-3 w-5/6" />
              <div className="skeleton h-3 w-2/3" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-label" style={{ textAlign: 'left' }}>
                  <th className="font-medium pb-2.5 pr-4">用户</th>
                  <th className="font-medium pb-2.5 text-right">费用 (¥)</th>
                </tr>
              </thead>
              <tbody>
                {(data?.topUsers || []).map((u, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
                    <td className="py-2 pr-4 text-xs">{displayUserId(u.userId)}</td>
                    <td className="py-2 text-right font-mono-num text-xs font-semibold" style={{ color: ACCENT }}>
                      {u.cost?.toFixed?.(2) ?? u.cost}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!firstLoading && !data?.topUsers?.length && <EmptyText text="暂无数据" />}
        </Panel>
        <Panel title="错误数" sub="时间范围内失败的操作数">
          {firstLoading ? (
            <div className="space-y-2">
              <div className="skeleton" style={{ height: 40, width: 96 }} />
              <div className="skeleton h-3 w-1/2" />
            </div>
          ) : (
            <>
              <div className="font-mono-num text-3xl leading-9 font-semibold" style={{ color: 'var(--color-error)' }}>
                {data?.failedCount ?? '-'}
              </div>
              <div className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>时间范围内失败的操作数</div>
            </>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default OverviewTab;
